/**
 * Decode HTML entities in a string (e.g., &#x27; → ', &amp; → &, &lt; → <).
 * Used to clean up data that was imported from websites with encoded characters.
 *
 * @param {string} str - The string to decode
 * @returns {string} Decoded string
 */
export function decodeHtmlEntities(str) {
  if (!str || typeof str !== 'string') return str;
  
  // Manually decode common entities to avoid regex escape issues
  // Note: must do &amp; LAST because all other codes contain &
  return str
    .replace(/&#(x)?0{0,8}27;/gi, "'")       // &#x27; &#39; &#x2019; etc
    .replace(/&#(x)?0{0,8}39;/gi, "'")        // &#39;
    .replace(/&#(x)[0-9a-fA-F]+;/g, (match) => {
      // Hex entities like &#x2019; &#x2018; &#x201C; &#x201D;
      const hex = match.replace(/&#x([0-9a-fA-F]+);/i, '$1');
      const code = parseInt(hex, 16);
      return isNaN(code) ? match : String.fromCharCode(code);
    })
    .replace(/&#\d+;/g, (match) => {
      // Decimal entities like &#8217; &#8216; &#8220; &#8221;
      const code = parseInt(match.replace(/[&#;]/g, ''), 10);
      return isNaN(code) ? match : String.fromCharCode(code);
    })
    .replace(/&quot;/g, '"')
    // &amp; must be last
    .replace(/&amp;/g, '&');
}

/**
 * Sanitize a string to prevent XSS attacks.
 * First decodes any existing HTML entities, then escapes the result.
 *
 * @param {string} str - The string to sanitize
 * @returns {string} Sanitized string - decoded entities, only &<> escaped (React handles the rest)
 */
export function escapeHtml(str) {
  if (str == null || str === '') return '';
  
  let string = decodeHtmlEntities(String(str));
  
  // Strip leading checkbox characters that sometimes end up in ingredient names from imports
  string = string.trimStart().replace(/^[▢☐☒✓✔•\-\*]+\s*/u, '');
  
  // Only escape & and <> for XSS safety — React's JSX text rendering handles the rest
  return string
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Sanitize an object's string fields recursively.
 * Useful for cleaning API responses before rendering.
 * 
 * @param {Object} obj - The object to sanitize
 * @param {string[]} [stringFields] - Specific fields to sanitize (null = all string fields)
 * @returns {Object} Sanitized object
 */
export function sanitizeObject(obj, stringFields = null) {
  if (obj == null) return obj;
  if (typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, stringFields));
  }
  
  const sanitized = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      sanitized[key] = stringFields === null || stringFields.includes(key) 
        ? escapeHtml(value) 
        : value;
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeObject(value, stringFields);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

/**
 * Truncate a string safely (after escaping).
 * 
 * @param {string} str - The string to truncate
 * @param {number} maxLength - Maximum length
 * @param {string} [suffix] - Suffix to append (default: '...')
 * @returns {string} Truncated, sanitized string
 */
export function truncateSafe(str, maxLength, suffix = '...') {
  const escaped = escapeHtml(str);
  if (escaped.length <= maxLength) return escaped;
  return escaped.substring(0, maxLength) + suffix;
}
